import ajax from '../utils/request'

/**
 * ArticleService
 * @module service/Article
 */
export default {
  /**
   * 获取文章列表(包括频道的列表)
   * @fires public/v1/article/getList:getArticleList
   * @param   {String}     data            参数
   * @param   {Number}    [data.id]        频道 ID
   * @param   {Number}    [data.type=1]    文章类型 [1:文章, 8: 电子书]
   * @param   {Number}    [data.score]     分页标识, 空值为第一页
   * @param   {Number}    [data.size=12]   页面大小
   * @param   {String}    [cancelToken]    取消请求的 token
   * @return  {Array<Object>}  res       数据基本与接口返回一致，追加了单独的作者或者译者的数据
   * @return  {Object}  res.authorData               单独的作者或者译者的数据
   * @return  {Number}  res.authorData.uid           作者或者译者的 ID
   * @return  {String}  res.authorData.nickname      作者或者译者的名称
   * @return  {String}  res.authorData.avatar        作者或者译者的头像地址
   * @return  {String}  res.authorData.authorLabel   作者或者译者的名词
   */
  getArticleList (data, cancelToken) {
    const postData = {
      type: data.type | 0 || 1,
      size: data.size | 0 || 12
    }

    // 频道 ID
    if (data.id) {
      postData.id = data.id - 0
    }

    // 分页标识 默认为第一页
    if (data.score) {
      postData.score = data.score - 0
    }

    /**
     * 获取文章列表
     * @see https://yapi.geekbang.org/project/116/interface/api/1223
     * @event public/v1/article/getList:getArticleList
     * @param {String}        data          data body
     * @param {Number}        data.id       频道 ID
     * @param {Number}        data.type     文章类型 [1:文章, 8: 电子书]
     * @param {Number}       [data.score]   分页标识, 空值为第一页
     * @param {Number}       [data.size]    页面大小
     * @param {String}        cancelToken   取消请求的 token
     * @return {Array<Object>}  res                     数据
     * @return {String}         res.id                  ID
     * @return {String}         res.aid                 文章 ID
     * @return {Number}         res.type                文章类型 [1:文章,8:电子书]
     * @return {Number}         res.ctype               是否是专题 [0:否,1:是]
     * @return {Number}         res.sub_type            是否是话题 [0:否,1:是]
     * @return {String}         res.views               阅读数
     * @return {String}         res.uuid                UUID
     * @return {String}         res.info                扩展数据
     * @return {String}         res.status              状态 [1:上架, 0:下架] 返回值只有1
     * @return {TimeStamp}      res.publish_time        发布时间
     * @return {TimeStamp}      res.ctime               创建时间
     * @return {TimeStamp}      res.utime               修改时间
     * @return {String}         res.article_title       标题
     * @return {String}         res.article_sharetitle  分享标题
     * @return {String}         res.article_subtitle    副标题
     * @return {String}         res.article_summary     摘要
     * @return {URL}            res.article_cover       封面
     * @return {String}         res.article_cover_point 图坐标组成
     * @return {Array<Object>}  res.author              作者
     * @return {String}         res.author.uid          作者 ID
     * @return {String}         res.author.nickname     作者名称
     * @return {URL}            res.author.avatar       译者头像
     * @return {Array<Object>}  res.translator          译者
     * @return {String}         res.translator.uid      译者 ID
     * @return {String}         res.translator.nickname 译者名称
     * @return {URL}            res.translator.avatar   译者头像
     * @return {Array<Object>}  res.topic               文章所属频道
     * @return {String}         res.topic.id            频道 ID
     * @return {String}         res.topic.name          频道标题
     * @return {Number}         res.love                点赞数
     */
    return ajax({
      url: 'public/v1/article/getList',
      data: postData,
      cancelToken
    })
    .then(res => {
      return res.map(article => this.parseArticleAuthor(article))
    })
  },
  /**
   * 获取文章详情
   * @emits public/v1/article/getDetail:getArticleDetail
   * @param {String} uuid 文章 UUID
   */
  getArticleDetail (uuid) {
    /**
     * 返回的数据
     * @see https://yapi.geekbang.org/project/116/interface/api/1233
     * @event public/v1/article/getDetail:getArticleDetail
     * @param {String}     id                  ID
     * @param {String}     aid                 文章 ID
     * @param {Number}     type                文章类型 [1:文章,8:电子书]
     * @param {Number}     res.ctype           是否是专题 [0:否,1:是]
     * @param {Number}     res.sub_type        是否是话题 [0:否,1:是]
     * @param {String}     views               阅读数
     * @param {String}     uuid                UUID
     * @param {String}     info                扩展数据
     * @param {String}     status              状态 [1:上架, 0:下架] 返回值只有1
     * @param {TimeStamp}  publish_time        发布时间
     * @param {TimeStamp}  ctime               创建时间
     * @param {TimeStamp}  utime               修改时间
     * @param {String}     article_title       标题
     * @param {String}     article_sharetitle  分享标题
     * @param {String}     article_subtitle    副标题
     * @param {String}     article_summary     摘要
     * @param {URL}        article_cover       封面
     * @param {String}     article_cover_point 图坐标组成
     * @param {Array}      author              作者
     * @param {String}     author.uid          作者 ID
     * @param {String}     author.nickname     作者名称
     * @param {URL}        author.avatar       译者头像
     * @param {Array}      translator          译者
     * @param {String}     translator.uid      译者 ID
     * @param {String}     translator.nickname 译者名称
     * @param {URL}        translator.avatar   译者头像
     * @param {Array}      topic               文章所属频道
     * @param {String}     topic.id            频道 ID
     * @param {String}     topic.name          频道标题
     * @param {Number}     love                点赞数
     * @param {String}     content             文章内容
     * @param {String}     ebook               电子数内容
     * @param {Array}      recommend_list      每项数据为一个 article
     * @param {Boolean}    isAWS               是否是AWS有关的频道
     */
    return ajax({
      url: 'public/v1/article/getDetail',
      data: {
        uuid
      }
    })
    .then(res => {
      res.recommend_list = res.recommend_list.map(article => this.parseArticleAuthor(article))
      // 循环频道  如果频道名称中包含aws 添加标志显示一条 类似广告的东西
      const reg = /aws/gi
      res.isAWS = !!res.topic.find(topic => reg.test(topic.name))

      return this.parseArticleAuthor(res)
    })
  },
  /**
   * 收藏/取消收藏文章
   * @param {String}  aid       文章 ID
   * @param {Boolean} favorite  是否收藏 [true:收藏, false:取消]
   * @param {Number}  [type]    类型 [1:专题]
   */
  favoriteArticle (aid, favorite, type) {
    const url = favorite ? 'public/v1/collect/add' : 'public/v1/collect/delete'
    const postData = {
      id: aid
    }

    if (type === 1) {
      postData.type = 1
    }

    return ajax({
      signin: true,
      url,
      data: postData
    })
  },
  /**
   * 关注/取消点赞文章
   * @param {String}  uuid      文章 ID
   * @param {Boolean} like  是否收藏 [true:收藏, false:取消]
   * @param {Number}  type  类型 [1:文章, 2:评论, 3回复]
   */
  likeArticle (uuid, like, type) {
    const url = like ? 'public/v1/user/like' : 'public/v1/user/cancelLike'

    return ajax({
      signin: true,
      url,
      data: {
        id: uuid,
        type: type | 0
      }
    })
  },
  /**
   * 获取文章的评论列表
   * @param {Object} data        接口参数
   * @param {String} data.id     文章 ID
   * @param {Number} [data.score]  分页标识
   * @param {Number} [data.size=20]   页面大小
   */
  getCommentList (data) {
    const postData = {
      id: data.id,
      size: data.size | 0 || 20
    }

    if (data.score) {
      postData.score = data.score - 0
    }

    return ajax({
      signin: true,
      url: 'public/v1/comment/getList',
      data: postData
    })
    /**
     * 返回的数据
     * @param {String}     id        评论 ID
     * @param {String}     aid       文章 ID
     * @param {String}     uid       用户 ID
     * @param {String}     content   评论内容
     * @param {Number}     status    状态
     * @param {TimeStamp}  ctime     创建日期
     * @param {TimeStamp}  utime     更新日期
     * @param {String}     score     分页标识
     * @param {Object}     user      评论的用户数据
     * @param {String}     user.uid  评论的用户 ID
     * @param {String}     user.nickname  评论的用户昵称
     */
    .then(res => res)
  },
  /**
   * 获取文章的精选评论列表
   * @param {Object} data        接口参数
   * @param {String} data.id     文章 ID
   * @param {Number} [data.score]  分页标识
   * @param {Number} [data.size=20]   页面大小
   */
  getRecommendCommentList (data) {
    const postData = {
      id: data.id,
      size: data.size | 0 || 10
    }

    if (data.score) {
      postData.score = data.score - 0
    }

    return ajax({
      signin: true,
      url: 'public/v1/comment/getRecommendList',
      data: postData
    })
    /**
     * 返回的数据
     * @param {String}     id        评论 ID
     * @param {String}     aid       文章 ID
     * @param {String}     uid       用户 ID
     * @param {String}     content   评论内容
     * @param {Number}     status    状态
     * @param {TimeStamp}  ctime     创建日期
     * @param {TimeStamp}  utime     更新日期
     * @param {String}     score     分页标识
     * @param {Object}     user      评论的用户数据
     * @param {String}     user.uid  评论的用户 ID
     * @param {String}     user.nickname  评论的用户昵称
     */
    .then(res => res)
  },
  /**
   * 添加评论
   * @param {Object} data          参数
   * @param {String} data.id       文章 aid
   * @param {String} data.content  评论内容
   * @param {Object} extra         额外的数据  例如：请求成功后的回调  根据数据类型  做一些操作
   */
  addComment (data, extra) {
    return ajax({
      signin: true,
      url: 'public/v1/comment/add',
      data,
      extra: extra
    }).then(res => {
      res.reply_list.list = res.reply_list.list || []
      res.reply_list.page = res.reply_list.page || {}
      return res
    })
  },
  /**
   * 删除评论
   * @param {String} id 评论 ID
   */
  deleteComment (id) {
    return ajax({
      signin: true,
      url: 'public/v1/comment/delComment',
      data: {
        id
      }
    })
  },
  /**
   * 获取文章的评论的回复列表
   * @param {Object} data        接口参数
   * @param {String} data.id     评论 ID
   * @param {String} data.score  分页标识
   * @param {Number} data.size   页面大小
   */
  getReplyList (data) {
    const postData = {
      id: data.id,
      size: data.size | 0 || 3
    }

    if (data.score) {
      postData.score = data.score - 0
    }

    return ajax({
      url: 'public/v1/comment/getReplyList',
      data: postData
    })
    /**
     * 返回的数据
     * @param {String}     id          回复 ID
     * @param {String}     aid         文章 ID
     * @param {String}     uid         用户 ID
     * @param {String}     comment_id  评论 ID
     * @param {String}     reply_id    评论 ID
     * @param {String}     reply_uid   评论 ID
     * @param {String}     content     评论内容
     * @param {Number}     status      状态
     * @param {TimeStamp}  ctime       创建日期
     * @param {TimeStamp}  utime       更新日期
     * @param {String}     score       分页标识
     * @param {Object}     user        评论的用户数据
     * @param {String}     user.uid    评论的用户 ID
     * @param {String}     user.nickname  评论的用户昵称
     * @param {Url}        user.avatar    评论的用户头像
     * @param {Object}     reply_user           评论的用户数据
     * @param {String}     reply_user.uid       评论的用户 ID
     * @param {String}     reply_user.nickname  评论的用户昵称
     * @param {Url}        reply_user.avatar    评论的用户头像
     */
    .then(res => res)
  },
  /**
   * 添加回复
   * @param {Object} data             参数
   * @param {String} data.aid         文章 ID
   * @param {String} data.comment_id  评论 ID
   * @param {String} data.content     评论内容
   * @param {String} data.reply_id    如果回复 回复,标示 回复 ID
   */
  addReply (data) {
    return ajax({
      signin: true,
      url: 'public/v1/comment/addReply',
      data
    })
  },
  /**
   * 删除回复
   * @param {String} id 回复 ID
   */
  deleteReply (id) {
    return ajax({
      signin: true,
      url: 'public/v1/comment/delReply',
      data: {
        id
      }
    })
  },
  /**
   * 整理电子书作者的数据
   * @param {Object} article 文章数据
   */
  parseArticleAuthor (article) {
    const author = Array.isArray(article.author) && article.author.length && article.author[0]
    const translate = Array.isArray(article.translate) && article.translate.length && article.translate[0]
    const isAuthor = !!author
    const authorData = author || translate || {}

    authorData.authorLabel = isAuthor ? '作者' : '译者'

    return Object.assign({
      authorData
    }, article)
  },
  /**
   * 获取第一作者信息
   * @param {Number} aid                         文章id
   * @param {Number} leadAuthorId                第一作者id
   */
  getLeadAuthorInfo (aid, leadAuthorId) {
    return ajax({
      url: 'public/v1/user/get_newest_list',
      data: {
        id: leadAuthorId,
        aid: aid
      }
    })
  }
}
